import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatRadioModule } from "@angular/material/radio";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatStepper } from "@angular/material/stepper";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { Subject, take, takeUntil } from "rxjs";

import {
  QualityMeasureQuestionType,
  QualityMeasureSurvey,
  QualityMeasureSurveyAnswer,
  QualityMeasureSurveyQuestion,
} from "@models/quality-measure-survey.model";
import { QualityMeasureSurveyActions } from "@store/actions/quality-measure-survey/quality-measure-survey.actions";
import {
  selectQualityMeasureSurvey,
  selectQualityMeasureSurveyExporting,
  selectQualityMeasureSurveyLoading,
  selectQualityMeasureSurveySaving,
} from "@store/selectors/quality-measure-survey/quality-measure-survey.selectors";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { UserService } from "@services/user.service";
import { ROLE_GROUPS } from "@constants/roles";
import { findEquivalentMeasureCode } from "@constants/measure-equivalency";

interface SingleRenderItem {
  kind: "single";
  question: QualityMeasureSurveyQuestion;
}

interface RadioGroupRenderItem {
  kind: "radioGroup";
  radioGroupId: string;
  questions: QualityMeasureSurveyQuestion[];
}

type SurveyRenderItem = SingleRenderItem | RadioGroupRenderItem;

interface SubSectionViewModel {
  id: number;
  name: string;
  items: SurveyRenderItem[];
}

interface SectionViewModel {
  id: number;
  name: string;
  subSections: SubSectionViewModel[];
}

export const RELEVANCE_OPTIONS = ["High", "Medium", "Low", "N/A"] as const;

// This is the value sent when checking a Checkbox/Radio answer — see QualityMeasureSurveyQuestion.value.
const CHECKED_ANSWER_VALUE = "1";

@Component({
  selector: "app-quality-measure-selection-survey",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: "./quality-measure-selection-survey.component.html",
  styleUrls: ["./quality-measure-selection-survey.component.css"],
})
export class QualityMeasureSelectionSurveyComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly actions$ = inject(Actions);
  private readonly destroy$ = new Subject<void>();

  readonly relevanceOptions = RELEVANCE_OPTIONS;

  isSuperAdmin$ = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);
  isLoading = toSignal(this.store.select(selectQualityMeasureSurveyLoading), { initialValue: true });
  isSaving = toSignal(this.store.select(selectQualityMeasureSurveySaving), { initialValue: false });
  isExporting = toSignal(this.store.select(selectQualityMeasureSurveyExporting), { initialValue: false });

  answersForm: FormGroup = new FormGroup({});
  sections: SectionViewModel[] = [];

  private survey: QualityMeasureSurvey | null = null;
  private organizationId: number | null = null;
  private hasBuiltForm = false;
  private readonly collapsedSubSectionKeys = new Set<string>();

  ngOnInit(): void {
    this.store
      .select(selectOrgId)
      .pipe(take(1))
      .subscribe((orgId) => {
        const organizationId = orgId ? Number(orgId) : 0;
        this.organizationId = organizationId;
        this.store.dispatch(QualityMeasureSurveyActions.loadSurvey({ organizationId }));
      });

    this.store
      .select(selectQualityMeasureSurvey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((survey) => {
        if (!survey) {
          return;
        }
        this.survey = survey;
        this.sections = this.buildViewModel(survey);

        // Only build the FormGroup from scratch on the initial load. The post-save reload
        // (reloadSurveyAfterSave$) exists to pick up server-side sibling-radio clearing and
        // sister-measure mirroring, but rebuilding the whole form on every emission would discard
        // any edit the user made to a different field while that reload was in flight.
        if (!this.hasBuiltForm) {
          this.answersForm = this.buildForm(survey);
          this.hasBuiltForm = true;
        } else {
          this.patchFormFromSurvey(survey);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveSurveyAndAdvance(stepper: MatStepper): void {
    this.saveSurveyThen(() => stepper.next());
  }

  saveSurveyAndGoBack(stepper: MatStepper): void {
    this.saveSurveyThen(() => stepper.previous());
  }

  // Used by the stepper's Back/Next buttons and Export so they wait for the save to actually
  // finish instead of acting on stale data. Only runs the callback on a confirmed success (or
  // immediately if there was nothing to save) — a failure leaves the user on this step so the
  // error dialog from saveSurveyFailure$ isn't shown a step late.
  private saveSurveyThen(onSaved: () => void): void {
    if (!this.survey || this.organizationId == null || this.organizationId === 0) {
      return;
    }

    const answers = this.computeAnswerDiffs();
    if (answers.length === 0) {
      onSaved();
      return;
    }

    this.actions$
      .pipe(
        ofType(QualityMeasureSurveyActions.saveSurveySuccess, QualityMeasureSurveyActions.saveSurveyFailure),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (action.type === QualityMeasureSurveyActions.saveSurveySuccess.type) {
          onSaved();
        }
      });

    this.store.dispatch(
      QualityMeasureSurveyActions.saveSurvey({
        organizationId: this.organizationId,
        answers,
      })
    );
  }

  // Reconciles the form with a fresh survey snapshot (from the post-save reload) without
  // clobbering edits the user made to other fields while that reload was in flight — only
  // pristine controls (ones the user hasn't touched since) are patched to the server's value.
  private patchFormFromSurvey(survey: QualityMeasureSurvey): void {
    for (const section of survey.sections) {
      for (const subSection of section.subSections) {
        for (const item of this.buildRenderItems(subSection.questions)) {
          if (item.kind === "radioGroup") {
            const control = this.answersForm.get(this.radioGroupControlKey(item.radioGroupId));
            if (!control || control.dirty) {
              continue;
            }
            const selectedQuestion = item.questions.find((question) => question.value != null);
            control.setValue(selectedQuestion?.id ?? null, { emitEvent: false });
          } else {
            const control = this.answersForm.get(this.questionControlKey(item.question.id));
            if (!control || control.dirty) {
              continue;
            }
            control.setValue(this.questionToFormValue(item.question), { emitEvent: false });
          }
        }
      }
    }
  }

  // Mirrors sister-measure answers client-side for immediate feedback. The server does this too
  // (keyed on the same measureCode pairs), so the post-save re-fetch should agree with this, not
  // fight it — see reloadSurveyAfterSave$ in the effects.
  onAnswerChange(question: QualityMeasureSurveyQuestion): void {
    const equivalentMeasureCode = findEquivalentMeasureCode(question.measureCode);
    if (!equivalentMeasureCode) {
      return;
    }

    const siblingQuestion = this.findQuestionByMeasureCode(equivalentMeasureCode);
    if (!siblingQuestion) {
      return;
    }

    const currentValue = this.answersForm.get(this.questionControlKey(question.id))?.value;
    this.answersForm
      .get(this.questionControlKey(siblingQuestion.id))
      ?.setValue(currentValue, { emitEvent: false });
  }

  getCharacterCount(question: QualityMeasureSurveyQuestion): number {
    const value = this.answersForm.get(this.questionControlKey(question.id))?.value || "";
    return value.length;
  }

  questionControlKey(questionId: number): string {
    return `question_${questionId}`;
  }

  radioGroupControlKey(radioGroupId: string): string {
    return `radioGroup_${radioGroupId}`;
  }

  isSubSectionCollapsed(sectionId: number, subSectionId: number): boolean {
    return this.collapsedSubSectionKeys.has(this.subSectionKey(sectionId, subSectionId));
  }

  toggleSubSection(sectionId: number, subSectionId: number): void {
    const key = this.subSectionKey(sectionId, subSectionId);
    if (this.collapsedSubSectionKeys.has(key)) {
      this.collapsedSubSectionKeys.delete(key);
    } else {
      this.collapsedSubSectionKeys.add(key);
    }
  }

  collapseAllSubSections(): void {
    for (const section of this.sections) {
      for (const subSection of section.subSections) {
        this.collapsedSubSectionKeys.add(this.subSectionKey(section.id, subSection.id));
      }
    }
  }

  expandAllSubSections(): void {
    this.collapsedSubSectionKeys.clear();
  }

  private subSectionKey(sectionId: number, subSectionId: number): string {
    return `${sectionId}:${subSectionId}`;
  }

  questionDisplayLabel(question: QualityMeasureSurveyQuestion): string {
    const measureLabel = this.measureCodeLabel(question);
    return measureLabel ? `${measureLabel}: ${question.name}` : question.name;
  }

  measureCodeLabel(question: QualityMeasureSurveyQuestion): string | null {
    return question.measureCodeDisplayName || question.measureCode;
  }

  // Saves any unsaved edits first so the exported PDF reflects the current form state rather
  // than whatever was last persisted — without this, editing a field and clicking Export
  // before navigating would silently export stale data.
  onExport(): void {
    this.saveSurveyThen(() =>
      this.store.dispatch(QualityMeasureSurveyActions.exportSurvey({ organizationId: this.organizationId! }))
    );
  }

  private buildViewModel(survey: QualityMeasureSurvey): SectionViewModel[] {
    return survey.sections.map((section) => ({
      id: section.id,
      name: section.name,
      subSections: section.subSections.map((subSection) => ({
        id: subSection.id,
        name: subSection.name,
        items: this.buildRenderItems(subSection.questions),
      })),
    }));
  }

  private buildRenderItems(questions: QualityMeasureSurveyQuestion[]): SurveyRenderItem[] {
    const items: SurveyRenderItem[] = [];
    const radioGroupItemIndexById = new Map<string, number>();

    for (const question of questions) {
      if (question.questionType === "Radio" && question.radioGroupId) {
        const existingItemIndex = radioGroupItemIndexById.get(question.radioGroupId);
        if (existingItemIndex !== undefined) {
          (items[existingItemIndex] as RadioGroupRenderItem).questions.push(question);
        } else {
          radioGroupItemIndexById.set(question.radioGroupId, items.length);
          items.push({ kind: "radioGroup", radioGroupId: question.radioGroupId, questions: [question] });
        }
      } else {
        items.push({ kind: "single", question });
      }
    }

    return items;
  }

  private buildForm(survey: QualityMeasureSurvey): FormGroup {
    const controls: { [key: string]: FormControl } = {};

    for (const section of survey.sections) {
      for (const subSection of section.subSections) {
        for (const item of this.buildRenderItems(subSection.questions)) {
          if (item.kind === "radioGroup") {
            const selectedQuestion = item.questions.find((question) => question.value != null);
            controls[this.radioGroupControlKey(item.radioGroupId)] = new FormControl(selectedQuestion?.id ?? null);
          } else {
            controls[this.questionControlKey(item.question.id)] = new FormControl(
              this.questionToFormValue(item.question)
            );
          }
        }
      }
    }

    return new FormGroup(controls);
  }

  // Builds the survey's current in-progress answers (reflecting live form state) keyed by
  // questionId, expressed in the same string convention the API uses.
  private buildCurrentAnswersByQuestionId(): Map<number, string | null> {
    const answers = new Map<number, string | null>();
    if (!this.survey) {
      return answers;
    }

    for (const section of this.survey.sections) {
      for (const subSection of section.subSections) {
        for (const item of this.buildRenderItems(subSection.questions)) {
          if (item.kind === "radioGroup") {
            const selectedQuestionId = this.answersForm.get(this.radioGroupControlKey(item.radioGroupId))?.value ?? null;
            for (const question of item.questions) {
              answers.set(question.id, question.id === selectedQuestionId ? CHECKED_ANSWER_VALUE : null);
            }
          } else {
            const controlValue = this.answersForm.get(this.questionControlKey(item.question.id))?.value;
            answers.set(item.question.id, this.formValueToAnswer(item.question.questionType, controlValue));
          }
        }
      }
    }

    return answers;
  }

  private computeAnswerDiffs(): QualityMeasureSurveyAnswer[] {
    if (!this.survey) {
      return [];
    }

    const currentAnswers = this.buildCurrentAnswersByQuestionId();
    const diffs: QualityMeasureSurveyAnswer[] = [];

    for (const section of this.survey.sections) {
      for (const subSection of section.subSections) {
        for (const question of subSection.questions) {
          const currentValue = currentAnswers.get(question.id) ?? null;
          if (currentValue !== (question.value ?? null)) {
            diffs.push({ questionId: question.id, value: currentValue });
          }
        }
      }
    }

    return diffs;
  }

  private findQuestionByMeasureCode(measureCode: string): QualityMeasureSurveyQuestion | undefined {
    if (!this.survey) {
      return undefined;
    }

    for (const section of this.survey.sections) {
      for (const subSection of section.subSections) {
        const match = subSection.questions.find((question) => question.measureCode === measureCode);
        if (match) {
          return match;
        }
      }
    }

    return undefined;
  }

  private questionToFormValue(question: QualityMeasureSurveyQuestion): unknown {
    switch (question.questionType) {
      case "Checkbox":
        return question.value != null;
      case "Date":
        return question.value ? this.parseLocalDateString(question.value) : null;
      case "Relevance":
      case "Dropdown":
      case "DateTime":
      case "TextArea":
      default:
        return question.value ?? "";
    }
  }

  private formValueToAnswer(questionType: QualityMeasureQuestionType, value: unknown): string | null {
    switch (questionType) {
      case "Checkbox":
        return value ? CHECKED_ANSWER_VALUE : null;
      case "Date":
        return value instanceof Date ? this.formatLocalDate(value) : null;
      case "Relevance":
      case "Dropdown":
      case "DateTime":
      case "TextArea":
        return value === "" || value == null ? null : String(value);
      default:
        return value == null ? null : String(value);
    }
  }

  // Parses a date-only API string ("2024-03-15") as local midnight rather than UTC midnight,
  // per this app's date-handling rule — see CLAUDE.md's Date Handling section.
  private parseLocalDateString(dateStr: string): Date {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
